import React, { useMemo, useState } from "react"
import { Box, Chip, CircularProgress, Grid, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { Link } from "react-router-dom"
import {
    useGetAllExperienceBalancesQuery,
    useGetAllExperienceCategoriesQuery,
    useGetAllExperienceLevelsQuery,
    useGetActiveSeasonQuery,
    useGetAllSeasonsQuery,
} from "../../../core/api/experience-api"
import ExportGridToExcel from "../ExportGridToExcel"

const baseColumns = [
    { field: "email", headerName: "Email", width: 260, renderCell: (p) =>
        <Link to={`/admin/experience/${encodeURIComponent(p.value)}`}>{p.value}</Link>
    },
    { field: "totalXp", headerName: "Total XP", width: 110 },
    { field: "currentLevel", headerName: "Level", width: 100 },
    { field: "lastClaimedLevel", headerName: "Claimed lvl", width: 110 },
    { field: "claimedBc", headerName: "Claimed BC", width: 120 },
    { field: "pendingRewardBelcoins", headerName: "Pending BC", width: 130 },
]

function Stat({ label, value, tone }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minWidth: 180 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "text.secondary" }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: tone || "inherit", mt: 0.5 }}>
                {value}
            </Typography>
        </Paper>
    )
}

// Балансы XP по сотрудникам. По умолчанию — активный сезон; выпадающим
// списком рядом с экспортом можно посмотреть/выгрузить замороженные балансы
// любого прошлого сезона (уровни/Claimed BC считаются по сетке того сезона).
export default function ExperienceBalanceControl() {
    const { data: activeSeason } = useGetActiveSeasonQuery()
    const { data: seasons = [] } = useGetAllSeasonsQuery()
    // null → «активный сезон» (бэкенд без параметра season отдаёт активный).
    const [selectedSeason, setSelectedSeason] = useState(null)
    const effectiveSeason = selectedSeason ?? activeSeason?.number ?? null

    const { data: balances = [], isLoading } = useGetAllExperienceBalancesQuery({
        isForActive: true,
        ...(selectedSeason != null ? { season: selectedSeason } : {}),
    })
    const { data: allLevels = [] } = useGetAllExperienceLevelsQuery()
    const { data: xpCategories = [] } = useGetAllExperienceCategoriesQuery()

    // Сетка уровней и категории — выбранного сезона, не активного.
    const levels = useMemo(
        () => allLevels.filter(l => l.season === effectiveSeason),
        [allLevels, effectiveSeason]
    )
    const seasonCategories = useMemo(
        () => xpCategories.filter(c => c.season === effectiveSeason && c.active !== false),
        [xpCategories, effectiveSeason]
    )

    const totals = useMemo(() => {
        const totalXp = balances.reduce((s, b) => s + (b.totalXp || 0), 0)
        const pendingBc = balances.reduce((s, b) => s + (b.pendingRewardBelcoins || 0), 0)
        const employeesWithXp = balances.filter(b => (b.totalXp || 0) > 0).length
        // Claimed BC = sum over employees of sum of rewardBelcoins for levels 1..lastClaimedLevel.
        const claimedBc = balances.reduce((sum, b) => {
            const upTo = b.lastClaimedLevel || 0
            const perEmp = levels.filter(l => l.level <= upTo).reduce((x, l) => x + (l.rewardBelcoins || 0), 0)
            return sum + perEmp
        }, 0)
        return { totalXp, pendingBc, claimedBc, employeesWithXp }
    }, [balances, levels])

    const rows = balances.map(b => {
        const upTo = b.lastClaimedLevel || 0
        const claimedBc = levels.filter(l => l.level <= upTo).reduce((x, l) => x + (l.rewardBelcoins || 0), 0)
        // Flatten categoryXp into discrete fields so DataGrid can render & export them.
        const flat = {}
        for (const c of seasonCategories) {
            flat[`cat_${c.name}`] = (b.categoryXp && b.categoryXp[c.name]) || 0
        }
        return { id: b.email, claimedBc, ...flat, ...b }
    })

    // Append one column per XP-category of the selected season.
    const categoryColumns = seasonCategories
        .map(c => ({
            field: `cat_${c.name}`,
            headerName: c.name,
            width: 110,
            type: "number",
            valueGetter: (params) => (params.row.categoryXp && params.row.categoryXp[c.name]) || 0,
        }))
    const columns = [...baseColumns, ...categoryColumns]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>XP balances</Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item><Stat label="Employees with XP" value={totals.employeesWithXp} /></Grid>
                <Grid item><Stat label="Total XP accrued" value={totals.totalXp} /></Grid>
                <Grid item><Stat label="Claimed belcoins" value={totals.claimedBc} tone="var(--xp-success)" /></Grid>
                <Grid item><Stat label="Pending BC to claim" value={totals.pendingBc} tone={totals.pendingBc ? "var(--xp-orange)" : undefined} /></Grid>
            </Grid>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <ExportGridToExcel
                    data={rows}
                    filename={effectiveSeason ? `xp-balances-season-${effectiveSeason}` : "xp-balances"}
                />
                {seasons.length > 0 && effectiveSeason != null && (
                    <TextField
                        select
                        size="small"
                        label="Season"
                        sx={{ minWidth: 200 }}
                        value={effectiveSeason}
                        onChange={e => setSelectedSeason(Number(e.target.value))}
                    >
                        {seasons.map(s => (
                            <MenuItem key={s.number} value={s.number}>
                                {s.title || `Сезон ${s.number}`}
                                {s.active && <Chip size="small" color="success" label="active" sx={{ ml: 1, height: 18 }} />}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Stack>

            <Paper sx={{ height: 640 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    disableSelectionOnClick
                />
            </Paper>
        </Box>
    )
}
